import { useCallback, useMemo, useState } from 'react';

import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useTranslate } from 'src/locales';
import { useGetEmailActivitiesQuery } from 'src/services/email-sync/email-sync.service';
import { EmailActivity, EmailSendStatus } from 'src/services/email-sync/email-sync.types';

import { debounce } from 'lodash';
import DateDisplay from 'src/components/date/date-display';
import EmptyContent from 'src/components/empty-content';
import Iconify from 'src/components/iconify';
import useTable from 'src/components/table/use-table';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

// ----------------------------------------------------------------------

export default function EmailActivitiesCard() {
    const { t } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Table state
    const {
        page,
        order,
        orderBy,
        rowsPerPage,
        onChangePage,
        onChangeRowsPerPage,
    } = useTable({
        defaultRowsPerPage: 10,
    });

    // Search state
    const [searchQuery, setSearchQuery] = useState('');

    // Dialog state
    const [selectedEmail, setSelectedEmail] = useState<EmailActivity | null>(null);
    const [openDialog, setOpenDialog] = useState(false);

    // Fetch email activities
    const { data, isLoading } = useGetEmailActivitiesQuery({
        page: page + 1, // API uses 1-based indexing
        per_page: rowsPerPage,
        search_query: searchQuery,
    });

    const handleSearchChange = useMemo(() =>
        debounce((event: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(event.target.value);
        }, 500),
        [setSearchQuery]
    );

    // Handle dialog open/close
    const handleOpenDialog = (email: EmailActivity) => {
        setSelectedEmail(email);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedEmail(null);
    };

    // Render status chip based on email status
    const renderStatusChip = (status: EmailSendStatus) => {
        let color: 'success' | 'error' | 'warning' | 'default' | 'info' = 'default';
        let label = status;

        switch (status) {
            case EmailSendStatus.SENT:
                color = 'success';
                label = t('email_activities.status.sent');
                break;
            case EmailSendStatus.FAILED:
                color = 'error';
                label = t('email_activities.status.failed');
                break;
            case EmailSendStatus.PENDING:
                color = 'warning';
                label = t('email_activities.status.pending');
                break;
            case EmailSendStatus.SCHEDULED:
                color = 'info';
                label = t('email_activities.status.scheduled');
                break;
            case EmailSendStatus.DRAFT:
                color = 'default';
                label = t('email_activities.status.draft');
                break;
            default:
                color = 'default';
                label = status;
        }

        return <Chip label={label} color={color} size="small" />;
    };


    const getRecipientDisplay = useCallback((recipients: { name?: string; email: string }[]) => {
        if (recipients.length === 0) return '-';

        // if one, show email
        if (recipients.length === 1) return recipients[0].name || recipients[0].email;

        // if many, show first + count
        return `${recipients[0].name || recipients[0].email} +${recipients.length - 1}`;
    }, []);

    // Render email activities table
    const renderTable = () => {
        if (isLoading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!data || data.results.length === 0) {
            return (
                <EmptyContent
                    title={t('email_activities.empty.title')}
                    description={t('email_activities.empty.description')}
                    sx={{ my: 5 }}
                />
            );
        }

        // Mobile view
        if (isMobile) {
            return (
                <>
                    <Stack spacing={2}>
                        {data.results.map((email: EmailActivity) => (
                            <Card key={email.id} sx={{ p: 2, boxShadow: 1 }}>
                                <Stack spacing={1}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap>
                                            {t('email_activities.table.subject')}
                                        </Typography>
                                        <TruncatedTextField
                                            text={email.subject}
                                            mode="characters"
                                            limit={20}
                                            showMoreEnabled={false}
                                            variant="body2"
                                        />
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap>
                                            {t('email_activities.table.from')}
                                        </Typography>
                                        <Typography variant="body2" noWrap>
                                            {email.from.name || email.from.email}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap>
                                            {t('email_activities.table.to')}
                                        </Typography>
                                        <Typography variant="body2" noWrap>
                                            {getRecipientDisplay(email.to)}
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap>
                                            {t('email_activities.table.sent_time')}
                                        </Typography>
                                        <DateDisplay date={new Date(email.id)} format="DD/MM/YYYY hh:mm A" variant="body2" />
                                    </Stack>

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle2" noWrap>
                                            {t('email_activities.table.status')}
                                        </Typography>
                                        {renderStatusChip(email.status)}
                                    </Stack>

                                    <Button
                                        fullWidth
                                        size="small"
                                        color="primary"
                                        onClick={() => handleOpenDialog(email)}
                                        startIcon={<Iconify icon="eva:eye-fill" />}
                                        sx={{ mt: 1 }}
                                    >
                                        {t('common.view')}
                                    </Button>
                                </Stack>
                            </Card>
                        ))}
                    </Stack>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={data.count}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={onChangePage}
                        onRowsPerPageChange={onChangeRowsPerPage}
                    />
                </>
            );
        }

        // Desktop view
        return (
            <>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('email_activities.table.from')}</TableCell>
                                <TableCell>{t('email_activities.table.to')}</TableCell>
                                <TableCell>{t('email_activities.table.subject')}</TableCell>
                                <TableCell>{t('email_activities.table.sent_time')}</TableCell>
                                <TableCell>{t('email_activities.table.attachments')}</TableCell>
                                <TableCell>{t('email_activities.table.status')}</TableCell>
                                <TableCell align="right">{t('email_activities.table.actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.results.map((email: EmailActivity) => (
                                <TableRow key={email.id} hover>
                                    <TableCell>
                                        <Stack direction="column" >
                                            <TruncatedTextField
                                                text={email.from.name}
                                                mode="characters"
                                                limit={30}
                                                showMoreEnabled={false}
                                                variant="subtitle2"
                                            />
                                            <TruncatedTextField
                                                text={email.from.email}
                                                mode="characters"
                                                limit={30}
                                                showMoreEnabled={false}
                                                variant="caption"
                                                color="text.secondary"
                                            />
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {email.to.length > 0
                                                ? (email.to[0].name || email.to[0].email) + (email.to.length > 1 ? ` +${email.to.length - 1}` : '')
                                                : '-'}
                                        </Typography>

                                    </TableCell>
                                    <TableCell>
                                        <TruncatedTextField
                                            text={email.subject}
                                            mode="characters"
                                            limit={30}
                                            showMoreEnabled={false}
                                            variant="body2"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <DateDisplay date={email.created_at} format="DD/MM/YYYY hh:mm A" variant="body2" />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {email.attachment_count > 0 ? email.attachment_count : '-'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {renderStatusChip(email.status)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(email)}
                                            startIcon={<Iconify icon="eva:eye-fill" />}
                                        >
                                            {t('common.view')}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={data.count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onChangePage}
                    onRowsPerPageChange={onChangeRowsPerPage}
                />
            </>
        );
    };

    // Render email details dialog
    const renderEmailDetailsDialog = () => {
        if (!selectedEmail) return null;

        return (
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: (themeB) => themeB.shadows[24],
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        pb: 1,
                        borderBottom: (themeB) => `1px solid ${themeB.palette.divider}`,
                        bgcolor: (themeB) => themeB.palette.mode === 'light'
                            ? themeB.palette.grey[50]
                            : themeB.palette.background.neutral
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1} justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Iconify
                                icon="mdi:email-outline"
                                sx={{
                                    width: 28,
                                    height: 28,
                                    color: 'primary.main'
                                }}
                            />
                            <Stack direction="column" spacing={0}>
                                <Typography variant="h6">{selectedEmail.subject}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <DateDisplay
                                        date={selectedEmail.created_at}
                                        format="DD MMM YYYY, hh:mm A"
                                        variant="caption"
                                        sx={{ color: 'text.secondary' }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                        {renderStatusChip(selectedEmail.status)}
                    </Stack>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, md: 3 },
                        bgcolor: (themeB) => themeB.palette.background.paper
                    }}
                >
                    <Stack spacing={3}>
                        {/* Email metadata section */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                bgcolor: (themeB) => themeB.palette.mode === 'light'
                                    ? themeB.palette.grey[50]
                                    : themeB.palette.background.neutral
                            }}
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2}>
                                        {/* From */}
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('email_activities.dialog.from')}
                                            </Typography>
                                            <Typography variant="body2">
                                                {selectedEmail.from.name ? (
                                                    <>
                                                        <Typography component="span" variant="subtitle2">
                                                            {selectedEmail.from.name}
                                                        </Typography>
                                                        <Typography component="span" variant="body2" color="text.secondary">
                                                            {' <'}{selectedEmail.from.email}{'>'}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    selectedEmail.from.email
                                                )}
                                            </Typography>
                                        </Stack>

                                        {/* To */}
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('email_activities.dialog.to')}
                                            </Typography>
                                            <Box>
                                                {selectedEmail.to.map((recipient, index) => (
                                                    <Typography key={`to-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                                                        {recipient.name ? (
                                                            <>
                                                                <Typography component="span" variant="subtitle2">
                                                                    {recipient.name}
                                                                </Typography>
                                                                <Typography component="span" variant="body2" color="text.secondary">
                                                                    {' <'}{recipient.email}{'>'}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            recipient.email
                                                        )}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Stack spacing={2}>
                                        {/* CC */}
                                        {selectedEmail.cc.length > 0 && (
                                            <Stack spacing={0.5}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {t('email_activities.dialog.cc')}
                                                </Typography>
                                                <Box>
                                                    {selectedEmail.cc.map((recipient, index) => (
                                                        <Typography key={`cc-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                                                            {recipient.name ? (
                                                                <>
                                                                    <Typography component="span" variant="subtitle2">
                                                                        {recipient.name}
                                                                    </Typography>
                                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                                        {' <'}{recipient.email}{'>'}
                                                                    </Typography>
                                                                </>
                                                            ) : (
                                                                recipient.email
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        )}

                                        {/* BCC */}
                                        {selectedEmail.bcc.length > 0 && (
                                            <Stack spacing={0.5}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    {t('email_activities.dialog.bcc')}
                                                </Typography>
                                                <Box>
                                                    {selectedEmail.bcc.map((recipient, index) => (
                                                        <Typography key={`bcc-${index}`} variant="body2" sx={{ mb: 0.5 }}>
                                                            {recipient.name ? (
                                                                <>
                                                                    <Typography component="span" variant="subtitle2">
                                                                        {recipient.name}
                                                                    </Typography>
                                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                                        {' <'}{recipient.email}{'>'}
                                                                    </Typography>
                                                                </>
                                                            ) : (
                                                                recipient.email
                                                            )}
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            </Stack>
                                        )}

                                        {/* Attachments */}
                                        <Stack spacing={0.5}>
                                            <Typography variant="subtitle2" color="text.secondary">
                                                {t('email_activities.dialog.attachments')}
                                            </Typography>
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                {selectedEmail.attachment_count > 0 ? (
                                                    <>
                                                        <Iconify icon="mdi:attachment" />
                                                        <Typography variant="body2">
                                                            {selectedEmail.attachment_count} {selectedEmail.attachment_count === 1
                                                                ? t('email_activities.dialog.attachment')
                                                                : t('email_activities.dialog.attachments')}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        {t('email_activities.dialog.no_attachments')}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Fail reason if applicable */}
                        {selectedEmail.status === EmailSendStatus.FAILED && selectedEmail.fail_reason && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: (themeB) => themeB.palette.error.lighter,
                                    border: (themeB) => `1px solid ${themeB.palette.error.light}`
                                }}
                            >
                                <Stack spacing={1}>
                                    <Typography variant="subtitle2" color="error.main">
                                        {t('email_activities.dialog.fail_reason')}
                                    </Typography>
                                    <Typography variant="body2" color="error.main">
                                        {selectedEmail.fail_reason}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}

                        {/* Email content */}
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                {t('email_activities.dialog.content')}
                            </Typography>
                            <Box
                                sx={{
                                    p: 2,
                                    border: (themeB) => `1px solid ${themeB.palette.divider}`,
                                    borderRadius: 1,
                                    maxHeight: '400px',
                                    overflow: 'auto',
                                    bgcolor: 'background.paper',
                                    '& img': {
                                        maxWidth: '100%',
                                        height: 'auto'
                                    }
                                }}
                            >
                                <div dangerouslySetInnerHTML={{ __html: selectedEmail.content }} />
                            </Box>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={handleCloseDialog}
                        variant="outlined"
                        color="primary"
                        startIcon={<Iconify icon="eva:close-fill" />}
                    >
                        {t('common.close')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    return (
        <Card sx={{ width: '100%', boxShadow: 0 }}>
            <CardHeader
                avatar={
                    <Iconify
                        icon="mdi:email-sync"
                        sx={{
                            width: 30,
                            height: 30,
                            color: theme.palette.primary.main,
                        }}
                    />
                }
                title={
                    <Typography variant="h6">{t('email_activities.title')}</Typography>
                }
                subheader={t('email_activities.description')}
            />

            <CardContent>
                <Stack spacing={3}>
                    <TextField
                        fullWidth
                        onChange={handleSearchChange}
                        placeholder={t('email_activities.search_placeholder')}
                        InputProps={{
                            startAdornment: (
                                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', mr: 1 }} />
                            ),
                        }}
                    />

                    {renderTable()}
                    {renderEmailDetailsDialog()}
                </Stack>
            </CardContent>
        </Card>
    );
}